import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { startOfWeek, addDays, addWeeks, subWeeks, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay, pointerWithin,  } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import Icon from '../../components/AppIcon';
import DayColumn from './components/DayColumn';
import AddTrabajoModal from './components/AddTrabajoModal';
import EditTrabajoModal from './components/EditTrabajoModal';
import DeleteTrabajoModal from './components/DeleteTrabajoModal';
import MoveModal from './components/MoveModal';
import IncidenciaModal from './components/IncidenciaModal';
import TrabajoCard from './components/TrabajoCard';
import { planTrabajoService, planTrabajoPiezasService, clienteConfigService } from '../../services/planTrabajoService';
import { supabase } from '../../lib/supabase';

// ─── Timezone-safe helpers ────────────────────────────────────────────────────
// Converts a local Date to YYYY-MM-DD without UTC shift
const toDateKey = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date?.getTime())) return null;
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  const d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parses YYYY-MM-DD to local Date without UTC shift
const parseLocalDate = (str) => {
  if (!str || typeof str !== 'string') return null;
  const parts = str?.split('-');
  if (parts?.length !== 3) return null;
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
};

const CLIENT_COLORS = [
  '#D6EF4E', '#60A5FA', '#F472B6', '#34D399', '#FBBF24',
  '#A78BFA', '#FB923C', '#38BDF8', '#4ADE80', '#F87171',
];

const ESTADO_CONFIG = {
  PENDIENTE:   { icon: '○', label: 'Pendiente',   cls: 'text-white/50' },
  RECIBIDO:    { icon: '📦', label: 'Recibido',    cls: 'text-blue-400' },
  EN_PROCESO:  { icon: '▶', label: 'En proceso',  cls: 'text-yellow-400' },
  COMPLETADO:  { icon: '✓', label: 'Completado',  cls: 'text-green-400' },
  INCIDENCIA:  { icon: '!', label: 'Incidencia',  cls: 'text-red-400' },
  CANCELADO:   { icon: '✕', label: 'Cancelado',   cls: 'text-white/30' },
};

const PIEZA_ESTADO_CONFIG = {
  PENDIENTE:   { icon: '○', cls: 'text-white/40' },
  EN_PROCESO:  { icon: '▶', cls: 'text-yellow-400' },
  COMPLETADA:  { icon: '✓', cls: 'text-green-400' },
};

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const PlanTrabajo = () => {
  // ── Single source of truth: weekStart is always a Monday Date object ──────
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  // Derived from weekStart — never stored as separate state
  const weekEnd = addDays(weekStart, 5); // Saturday
  const weekDays = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i)); // Mon–Sat Date objects
  const weekDates = weekDays?.map(toDateKey); // YYYY-MM-DD strings for Supabase queries

  const weekLabel = (() => {
    const startStr = format(weekStart, 'd MMM', { locale: es });
    const endStr = format(weekEnd, 'd MMM yyyy', { locale: es });
    return `${startStr} – ${endStr}`;
  })();

  const [trabajos, setTrabajos] = useState([]);
  const [unscheduled, setUnscheduled] = useState([]);
  const [clienteColors, setClienteColors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default to HOY on mobile, planificacion on desktop
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'trabajador';
    return 'planificacion';
  });

  // Mobile week sub-view: 'hoy' | 'semana'
  const [mobileWeekView, setMobileWeekView] = useState('hoy');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalDate, setAddModalDate] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingTrabajo, setMovingTrabajo] = useState(null);
  const [showIncidenciaModal, setShowIncidenciaModal] = useState(false);
  const [incidenciaTrabajo, setIncidenciaTrabajo] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTrabajo, setEditingTrabajo] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTrabajo, setDeletingTrabajo] = useState(null);

  // Filters
  const [filterCliente, setFilterCliente] = useState('all');
  const [filterEmpleado, setFilterEmpleado] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [filterUrgentes, setFilterUrgentes] = useState(false);

  // Dropdown data
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);

  // DnD state
  const [activeId, setActiveId] = useState(null);
  const [activeTrabajo, setActiveTrabajo] = useState(null);

  // Portapapeles operativo: copiar un trabajo y pegarlo en uno o varios días.
  const [copiedTrabajo, setCopiedTrabajo] = useState(null);
  const [copyPasting, setCopyPasting] = useState(false);

  useEffect(() => {
    console.log('[PlanTrabajo] weekStart', weekStart);
    console.log('[PlanTrabajo] weekEnd', weekEnd);
    console.log('[PlanTrabajo] from', toDateKey(weekStart));
    console.log('[PlanTrabajo] to', toDateKey(weekEnd));
    loadData();
  }, [weekStart]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Generar trabajos recurrentes antes de cargar (idempotente, no duplica)
      try { await planTrabajoService?.generateRecurrentes(); } catch (e) { console.warn('[PlanTrabajo] generateRecurrentes:', e?.message); }

      const fromDate = toDateKey(weekStart);
      let toDate = toDateKey(weekEnd);

      const [trabajosData, unscheduledData, clientesData, empleadosData, configs] = await Promise.all([
        planTrabajoService?.getByWeek(weekStart),
        planTrabajoService?.getUnscheduled(),
        supabase?.from('clientes')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
        supabase?.from('empleados')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
        clienteConfigService?.getAll(),
      ]);

      console.log('[PlanTrabajo] trabajos cargados', trabajosData);

      const colorMap = {};
      configs?.forEach(c => { if (c?.cliente_id && c?.color) colorMap[c.cliente_id] = c?.color; });
      (clientesData?.data || [])?.forEach((c, i) => {
        if (!colorMap?.[c?.id]) colorMap[c.id] = CLIENT_COLORS?.[i % CLIENT_COLORS?.length];
      });
      setClienteColors(colorMap);

      const enrich = (t) => ({ ...t, clienteColor: colorMap?.[t?.cliente_id] || '#6B7280' });
      setTrabajos((trabajosData || [])?.map(enrich));
      setUnscheduled((unscheduledData || [])?.map(enrich));
      setClientes(clientesData?.data || []);
      setEmpleados(empleadosData?.data || []);
    } catch (err) {
      console.error('[PlanTrabajo.loadData]', err);
      setError(err?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (list) => {
    let filtered = [...list];
    if (filterEstado !== 'CANCELADO') {
      filtered = filtered?.filter(t => t?.estado !== 'CANCELADO');
    }
    if (filterCliente !== 'all') filtered = filtered?.filter(t => t?.cliente_id === filterCliente);
    if (filterEmpleado !== 'all') filtered = filtered?.filter(t => t?.empleado_id === filterEmpleado);
    if (filterEstado !== 'all') filtered = filtered?.filter(t => t?.estado === filterEstado);
    if (filterUrgentes) filtered = filtered?.filter(t => t?.prioridad === 'URGENTE');
    return filtered;
  };

  // Compare trabajo.fecha (YYYY-MM-DD string) against toDateKey(dayDate)
  const getTrabajosByDate = (dateKey) => applyFilters(trabajos?.filter(t => t?.fecha === dateKey));

  // ── Navigation ────────────────────────────────────────────────────────────
  const goToPrevWeek = () => setWeekStart(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setWeekStart(prev => addWeeks(prev, 1));
  const goToToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // ── DnD sensors — supports mouse, touch, pointer ──────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }, // 8px before drag starts (allows click)
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 }, // 200ms hold on touch
    })
  );

  // ── DnD handlers ──────────────────────────────────────────────────────────
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active?.id);
    // Find the trabajo being dragged (from any day or unscheduled)
    const found =
      trabajos?.find(t => t?.id === active?.id) ||
      unscheduled?.find(t => t?.id === active?.id);
    setActiveTrabajo(found || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveTrabajo(null);

    if (!over) return;

    const trabajoId = active?.id;
    const trabajo = trabajos?.find(t => t?.id === trabajoId) || unscheduled?.find(t => t?.id === trabajoId);
    if (!trabajo) return;

    // Block COMPLETADO from being moved
    if (trabajo?.estado === 'COMPLETADO') return;

    // Determine source date
    const fromDate = trabajo?.fecha || null;

    // Determine destination: over can be a column droppable or another card (sortable)
    const overId = over?.id;
    const overData = over?.data?.current;

    let toDate = null;
    let insertPosition = null;

    if (overData?.type === 'column') {
      // Dropped directly on a column (empty area)
      toDate = overData?.date || null;
      // Insert at end of destination day
      const destItems = trabajos?.filter(t => t?.fecha === toDate && t?.id !== trabajoId);
      insertPosition = destItems?.length;
    } else if (overData?.type === 'trabajo') {
      // Dropped on another card — find which column it belongs to
      const overTrabajo = trabajos?.find(t => t?.id === overId) || unscheduled?.find(t => t?.id === overId);
      toDate = overTrabajo?.fecha || null;
      // Find the index of the over card in the destination day
      const destItems = trabajos?.filter(t => t?.fecha === toDate);
      const overIndex = destItems?.findIndex(t => t?.id === overId);
      insertPosition = overIndex >= 0 ? overIndex : destItems?.length;
    } else if (String(overId)?.startsWith('col-')) {
      // Dropped on column droppable id
      const colDate = String(overId)?.replace('col-', '');
      toDate = colDate === 'unscheduled' ? null : colDate;
      const destItems = trabajos?.filter(t => t?.fecha === toDate && t?.id !== trabajoId);
      insertPosition = destItems?.length;
    } else {
      // Fallback: treat over as a card in the same column (same-day reorder)
      toDate = fromDate;
      const dayItems = trabajos?.filter(t => t?.fecha === fromDate);
      const oldIndex = dayItems?.findIndex(t => t?.id === trabajoId);
      const newIndex = dayItems?.findIndex(t => t?.id === overId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(dayItems, oldIndex, newIndex);
        // Optimistic update
        setTrabajos(prev => {
          const others = prev?.filter(t => t?.fecha !== fromDate);
          return [...others, ...reordered?.map((t, i) => ({ ...t, orden_dia: i }))];
        });
        try {
          const updates = reordered?.map((t, i) => ({ id: t?.id, orden_dia: i }));
          await planTrabajoService?.reorderDay(updates);
        } catch (err) {
          console.error('[PlanTrabajo.handleDragEnd reorder]', err);
          await loadData();
        }
      }
      return;
    }

    // Same day reorder via card-over-card
    if (toDate === fromDate && insertPosition !== null) {
      const dayItems = trabajos?.filter(t => t?.fecha === fromDate);
      const oldIndex = dayItems?.findIndex(t => t?.id === trabajoId);
      if (oldIndex !== -1 && oldIndex !== insertPosition) {
        const reordered = arrayMove(dayItems, oldIndex, insertPosition);
        setTrabajos(prev => {
          const others = prev?.filter(t => t?.fecha !== fromDate);
          return [...others, ...reordered?.map((t, i) => ({ ...t, orden_dia: i }))];
        });
        try {
          const updates = reordered?.map((t, i) => ({ id: t?.id, orden_dia: i }));
          await planTrabajoService?.reorderDay(updates);
        } catch (err) {
          console.error('[PlanTrabajo.handleDragEnd same-day reorder]', err);
          await loadData();
        }
      }
      return;
    }

    // Cross-day move (or to/from unscheduled)
    if (toDate !== fromDate) {
      // Check if RECURRENTE — show MoveModal instead
      if (trabajo?.tipo_trabajo === 'RECURRENTE') {
        setMovingTrabajo(trabajo);
        setShowMoveModal(true);
        return;
      }

      // Optimistic UI update
      setTrabajos(prev => {
        const withoutMoved = prev?.filter(t => t?.id !== trabajoId);
        if (!toDate) return withoutMoved; // moved to unscheduled
        const destItems = withoutMoved?.filter(t => t?.fecha === toDate);
        const pos = Math.max(0, Math.min(insertPosition ?? destItems?.length, destItems?.length));
        destItems?.splice(pos, 0, { ...trabajo, fecha: toDate });
        const srcItems = withoutMoved?.filter(t => t?.fecha === fromDate);
        const others = withoutMoved?.filter(t => t?.fecha !== toDate && t?.fecha !== fromDate);
        return [
          ...others,
          ...srcItems?.map((t, i) => ({ ...t, orden_dia: i })),
          ...destItems?.map((t, i) => ({ ...t, orden_dia: i })),
        ];
      });
      if (!toDate) {
        setUnscheduled(prev => [{ ...trabajo, fecha: null, orden_dia: null }, ...prev]);
      } else {
        setUnscheduled(prev => prev?.filter(t => t?.id !== trabajoId));
      }

      try {
        await planTrabajoService?.moveTrabajoToDateAndPosition(
          trabajoId,
          toDate,
          insertPosition ?? 9999
        );
        await loadData(); // reload to get fresh data
      } catch (err) {
        console.error('[PlanTrabajo.handleDragEnd cross-day]', err);
        await loadData();
      }
    }
  };

  // ── "Mover a..." manual menu handler ─────────────────────────────────────
  const handleMoverAFecha = async (trabajoId, fechaDestino) => {
    const trabajo = trabajos?.find(t => t?.id === trabajoId) || unscheduled?.find(t => t?.id === trabajoId);
    if (!trabajo) return;

    // For RECURRENTE, use the existing MoveModal
    if (trabajo?.tipo_trabajo === 'RECURRENTE') {
      setMovingTrabajo(trabajo);
      setShowMoveModal(true);
      return;
    }

    try {
      // Insert at end of destination day
      const destItems = trabajos?.filter(t => t?.fecha === fechaDestino);
      const insertPos = destItems?.length;
      await planTrabajoService?.moveTrabajoToDateAndPosition(trabajoId, fechaDestino, insertPos);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleMoverAFecha]', err);
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleDrop = async (trabajoId, newDate, tipoTrabajo) => {
    if (tipoTrabajo === 'RECURRENTE') {
      setMovingTrabajo(trabajos?.find(t => t?.id === trabajoId) || unscheduled?.find(t => t?.id === trabajoId));
      setShowMoveModal(true);
      return;
    }
    try {
      await planTrabajoService?.moveToDate(trabajoId, newDate);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleDrop]', err);
    }
  };

  const handleEstadoChange = async (trabajoId, newEstado) => {
    try {
      if (newEstado === 'URGENTE') {
        await planTrabajoService?.update(trabajoId, { prioridad: 'URGENTE' });
      } else {
        await planTrabajoService?.updateEstado(trabajoId, newEstado);
      }
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleEstadoChange]', err);
    }
  };

  const handleMarcarRecibido = async (trabajo) => {
    try {
      await planTrabajoService?.marcarRecibido(trabajo?.id);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleMarcarRecibido]', err);
    }
  };

  const handleCopiarTrabajo = (trabajo) => {
    if (!trabajo?.cliente_id || !trabajo?.producto_id) return;
    setCopiedTrabajo(trabajo);
  };

  const handlePegarTrabajo = async (fechaDestino) => {
    if (!copiedTrabajo?.id || copyPasting) return;
    setCopyPasting(true);
    try {
      await planTrabajoService?.duplicate(copiedTrabajo.id, fechaDestino);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handlePegarTrabajo]', err);
      setError(err?.message || 'No se pudo copiar el trabajo');
    } finally {
      setCopyPasting(false);
    }
  };

  const handleAjustarPiezas = async (trabajo, delta) => {
    const newCount = (trabajo?.cantidad_piezas || 1) + delta;
    if (newCount < 1) return;
    try {
      await planTrabajoService?.adjustPiezas(trabajo?.id, newCount, trabajo?.producto_id);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleAjustarPiezas]', err);
    }
  };

  const handleAddTrabajo = (date) => {
    setAddModalDate(date || '');
    setShowAddModal(true);
  };

  const handleConfirmAdd = async (trabajoData) => {
    await planTrabajoService?.create(trabajoData);
    await loadData();
  };

  const handleEditar = (trabajo) => {
    setEditingTrabajo(trabajo);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (trabajoId, updates) => {
    await planTrabajoService?.edit(trabajoId, updates);
    await loadData();
  };

  const handleEliminar = (trabajo) => {
    setDeletingTrabajo(trabajo);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (trabajo) => {
    try {
      await planTrabajoService?.delete(trabajo?.id);
      setShowDeleteModal(false);
      setDeletingTrabajo(null);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleConfirmDelete]', err);
    }
  };

  const handleConfirmCancel = async (trabajo) => {
    try {
      await planTrabajoService?.cancel(trabajo?.id);
      setShowDeleteModal(false);
      setDeletingTrabajo(null);
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleConfirmCancel]', err);
    }
  };

  const handleMove = async (trabajoId, newDate, moveType) => {
    await planTrabajoService?.moveToDate(trabajoId, newDate);
    if (moveType === 'recurrencia' && movingTrabajo?.configuracion_origen_id) {
      const parsedDate = parseLocalDate(newDate);
      const jsDow = parsedDate?.getDay(); // 0=Dom, 1=Lun..6=Sáb
      const diaSemana = jsDow === 0 ? 7 : jsDow;
      await clienteConfigService?.upsert({
        id: movingTrabajo?.configuracion_origen_id,
        cliente_id: movingTrabajo?.cliente_id,
        dia_semana: diaSemana,
      });
    }
    await loadData();
  };

  const handleCopyPreviousWeek = async () => {
    if (!window.confirm('¿Copiar trabajos puntuales de la semana anterior?')) return;
    try {
      await planTrabajoService?.copyPreviousWeek(toDateKey(weekStart));
      await loadData();
    } catch (err) {
      console.error('[PlanTrabajo.handleCopyPreviousWeek]', err);
    }
  };

  const handleEmpezarPieza = async (trabajo, pieza) => {
    try {
      // Persist empleado_id to the piece row in Supabase (not just pass via URL)
      await planTrabajoPiezasService?.iniciarPiezaConEmpleado(pieza?.id, trabajo?.empleado_id);
    } catch (err) {
      console.error('[PlanTrabajo.handleEmpezarPieza iniciar]', err);
    }

    // Navigate with only IDs — piece-registration-form will reload all data from Supabase
    // using the shared loadPlanTrabajoContext function
    const params = new URLSearchParams({
      trabajo: trabajo?.id || '',
      pieza: pieza?.id || '',
    });

    window.location.href = `/piece-registration-form?${params?.toString()}`;
  };

  const handleVerRegistro = (pieza) => {
    if (pieza?.loncheado_id) {
      window.location.href = `/piece-registration-form?id=${pieza?.loncheado_id}`;
    }
  };

  const handleEmpezar = async (trabajo) => {
    let nextPieza = null;
    try {
      nextPieza = await planTrabajoPiezasService?.getNextPendiente(trabajo?.id);
      if (nextPieza) {
        await planTrabajoPiezasService?.iniciarPieza(nextPieza?.id);
      }
    } catch (err) {
      console.error('[PlanTrabajo.handleEmpezar pieza]', err);
    }

    const params = new URLSearchParams({
      clientId: trabajo?.cliente_id || '',
      productId: trabajo?.producto_id || '',
      employeeId: trabajo?.empleado_id || '',
      trabajoId: trabajo?.id || '',
      tipoCorte: trabajo?.tipo_corte || '',
      emplatado: trabajo?.emplatado ? '1' : '0',
      formatoGramos: trabajo?.formato_gramos || '',
      formatoCorte: trabajo?.formato_corte || '',
      observaciones: trabajo?.observaciones || '',
    });

    if (nextPieza?.id) params?.set('piezaId', nextPieza?.id);

    window.location.href = `/piece-registration-form?${params?.toString()}`;
  };

  const handleIncidencia = (trabajo) => {
    setIncidenciaTrabajo(trabajo);
    setShowIncidenciaModal(true);
  };

  const handleReorderDay = async (date, reorderedTrabajos) => {
    try {
      const updates = reorderedTrabajos?.map((t, i) => ({ id: t?.id, orden_dia: i }));
      await planTrabajoService?.reorderDay(updates);
      setTrabajos(prev => {
        const others = prev?.filter(t => t?.fecha !== date);
        return [...others, ...reorderedTrabajos?.map((t, i) => ({ ...t, orden_dia: i }))];
      });
    } catch (err) {
      console.error('[PlanTrabajo.handleReorderDay]', err);
    }
  };

  const todayKey = toDateKey(new Date());

  // Worker mode data — ordered by orden_dia
  const todayTrabajos = trabajos
    ?.filter(t => t?.fecha === todayKey && t?.estado !== 'COMPLETADO' && t?.estado !== 'CANCELADO')
    ?.sort((a, b) => (a?.orden_dia ?? 999) - (b?.orden_dia ?? 999));

  const totalPiezasHoy = todayTrabajos?.reduce((s, t) => {
    const piezas = t?.piezas || [];
    const pendientes = piezas?.filter(p => p?.estado !== 'COMPLETADA')?.length;
    return s + (pendientes || t?.cantidad_piezas || 1);
  }, 0);

  const nextTrabajo = todayTrabajos?.find(t => t?.estado === 'PENDIENTE' || t?.estado === 'RECIBIDO' || t?.estado === 'EN_PROCESO');

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet><title>Plan de Trabajo · Las Dos Doncellas</title></Helmet>

      <div className="plan-trabajo-page flex flex-col h-full min-h-screen" style={{ background: '#0A0A0A' }}>

        {/* ── Top bar ── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 flex-shrink-0">
          <div>
            <h1 className="font-oranienbaum font-bold text-white text-xl">Plan de Trabajo</h1>
            <p className="text-white/40 text-xs font-oranienbaum hidden sm:block">{weekLabel}</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              <button
                onClick={() => setViewMode('trabajador')}
                className={`px-3 py-1.5 text-xs font-oranienbaum transition-colors ${viewMode === 'trabajador' ? 'bg-[#D6EF4E]/15 text-[#D6EF4E]' : 'text-white/50 hover:text-white/70'}`}
              >
                HOY
              </button>
              <button
                onClick={() => setViewMode('planificacion')}
                className={`px-3 py-1.5 text-xs font-oranienbaum transition-colors ${viewMode === 'planificacion' ? 'bg-[#D6EF4E]/15 text-[#D6EF4E]' : 'text-white/50 hover:text-white/70'}`}
              >
                Semana
              </button>
            </div>

            {viewMode === 'planificacion' && (
              <>
                <button
                  onClick={handleCopyPreviousWeek}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-white/50 text-xs font-oranienbaum hover:border-white/25 hover:text-white/70 transition-colors hidden md:flex items-center gap-1.5"
                >
                  <Icon name="Copy" size={12} /> Copiar semana anterior
                </button>
                <button
                  onClick={() => handleAddTrabajo('')}
                  className="px-3 py-2 rounded-xl font-oranienbaum font-bold text-black text-xs transition-all hover:opacity-90 flex items-center gap-1.5"
                  style={{ background: '#D6EF4E' }}
                >
                  <Icon name="Plus" size={14} /> Añadir
                </button>
              </>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════════
            WORKER MODE — HOY (Mobile-first)
        ══════════════════════════════════════════════════════════════════════ */}
        {viewMode === 'trabajador' && (
          <div className="flex-1 overflow-y-auto">
            {/* Sub-tabs: HOY / SEMANA */}
            <div className="flex border-b border-white/8 px-4">
              <button
                onClick={() => setMobileWeekView('hoy')}
                className={`py-2.5 px-4 text-sm font-oranienbaum border-b-2 transition-colors ${mobileWeekView === 'hoy' ? 'border-[#D6EF4E] text-[#D6EF4E]' : 'border-transparent text-white/40 hover:text-white/60'}`}
              >
                HOY
              </button>
              <button
                onClick={() => setMobileWeekView('semana')}
                className={`py-2.5 px-4 text-sm font-oranienbaum border-b-2 transition-colors ${mobileWeekView === 'semana' ? 'border-[#D6EF4E] text-[#D6EF4E]' : 'border-transparent text-white/40 hover:text-white/60'}`}
              >
                SEMANA
              </button>
            </div>

            {/* ── HOY view ── */}
            {mobileWeekView === 'hoy' && (
              <div className="p-4 max-w-lg mx-auto space-y-4">
                {/* Date + summary */}
                <div className="text-center py-3">
                  <div className="text-white/40 text-sm font-oranienbaum capitalize">
                    {new Date()?.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                  <div className="text-white font-oranienbaum font-bold text-5xl mt-1">{totalPiezasHoy}</div>
                  <div className="text-white/40 font-oranienbaum text-sm">piezas pendientes</div>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
                  </div>
                ) : todayTrabajos?.length > 0 ? (
                  <div className="space-y-4">
                    {todayTrabajos?.map((trabajo, trabajoIdx) => {
                      const piezas = trabajo?.piezas || [];
                      const completadas = piezas?.filter(p => p?.estado === 'COMPLETADA')?.length;
                      const total = trabajo?.cantidad_piezas || piezas?.length || 1;
                      const color = trabajo?.clienteColor || '#6B7280';
                      const estadoCfg = ESTADO_CONFIG?.[trabajo?.estado] || ESTADO_CONFIG?.PENDIENTE;
                      const isFirst = trabajoIdx === 0;

                      return (
                        <div
                          key={trabajo?.id}
                          className="rounded-2xl border overflow-hidden"
                          style={{
                            background: '#111111',
                            borderColor: isFirst ? `${color}40` : 'rgba(255,255,255,0.08)',
                          }}
                        >
                          {/* Trabajo header */}
                          <div
                            className="px-4 py-3 border-b border-white/8 flex items-center justify-between"
                            style={isFirst ? { background: `${color}10` } : {}}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="text-white/30 text-xs font-oranienbaum flex-shrink-0">{trabajoIdx + 1}</span>
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
                              <span className="font-oranienbaum font-bold text-white text-lg truncate">{trabajo?.cliente?.nombre}</span>
                            </div>
                            <span className={`text-xs font-oranienbaum flex-shrink-0 ${estadoCfg?.cls}`}>
                              {estadoCfg?.icon} {estadoCfg?.label}
                            </span>
                          </div>

                          <div className="p-4">
                            {/* Producto + condiciones */}
                            <div className="text-white/60 font-oranienbaum text-base mb-1">{trabajo?.producto?.nombre}</div>
                            <div className="flex flex-wrap items-center gap-2 text-white/40 text-sm font-oranienbaum mb-2">
                              {trabajo?.tipo_corte && <span>{trabajo?.tipo_corte}</span>}
                              {trabajo?.formato_corte && trabajo?.formato_corte !== 'ENTERO' && (
                                <span>· {trabajo?.formato_corte?.replace('G', 'g')?.replace('KG', 'kg')}</span>
                              )}
                            </div>

                            {/* Badges */}
                            {(trabajo?.quiere_huesos || trabajo?.guardar_merma || trabajo?.observaciones) && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {trabajo?.quiere_huesos && (
                                  <span className="text-sm text-amber-400 font-oranienbaum bg-amber-400/10 px-2.5 py-1 rounded-lg border border-amber-400/20">🦴 GUARDAR HUESOS</span>
                                )}
                                {trabajo?.guardar_merma && (
                                  <span className="text-sm text-green-400 font-oranienbaum bg-green-400/10 px-2.5 py-1 rounded-lg border border-green-400/20">♻ GUARDAR MERMA</span>
                                )}
                                {trabajo?.observaciones && (
                                  <div className="w-full p-2.5 rounded-lg bg-blue-400/8 border border-blue-400/15 text-sm text-blue-300/80 font-oranienbaum italic">
                                    📝 {trabajo?.observaciones}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Progress */}
                            <div className="flex items-center justify-between text-xs font-oranienbaum mb-2">
                              <span className="text-white/40">Progreso</span>
                              <span className="text-white/80 font-bold">{completadas} / {total}</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/8 overflow-hidden mb-3">
                              <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{ width: `${total > 0 ? (completadas / total) * 100 : 0}%`, background: color }}
                              />
                            </div>

                            {/* Individual piezas */}
                            <div className="space-y-2 mb-3">
                              {piezas?.map((pieza) => {
                                const pEstado = PIEZA_ESTADO_CONFIG?.[pieza?.estado] || PIEZA_ESTADO_CONFIG?.PENDIENTE;
                                const isPendiente = pieza?.estado === 'PENDIENTE';
                                const isEnProceso = pieza?.estado === 'EN_PROCESO';
                                const isCompletada = pieza?.estado === 'COMPLETADA';

                                return (
                                  <div
                                    key={pieza?.id}
                                    className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                                      isEnProceso ? 'border-yellow-400/30 bg-yellow-400/5'
                                        : isCompletada ? 'border-green-400/20 bg-green-400/5' :'border-white/8 bg-white/3'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      <span className={`text-base flex-shrink-0 ${pEstado?.cls}`}>{pEstado?.icon}</span>
                                      <div className="min-w-0">
                                        <div className="text-white font-oranienbaum font-semibold text-base">
                                          Jamón {pieza?.numero_pieza}
                                        </div>
                                        <div className="text-white/40 text-xs font-oranienbaum">
                                          {trabajo?.producto?.nombre}
                                        </div>
                                      </div>
                                    </div>
                                    {(isPendiente || isEnProceso) && (
                                      <button
                                        onClick={() => handleEmpezarPieza(trabajo, pieza)}
                                        className="px-4 py-2.5 rounded-xl font-oranienbaum font-bold text-black text-sm transition-all hover:opacity-90 active:scale-[0.97] flex-shrink-0"
                                        style={{ background: isEnProceso ? '#FBBF24' : '#D6EF4E' }}
                                      >
                                        {isEnProceso ? '▶ CONTINUAR' : 'EMPEZAR'}
                                      </button>
                                    )}
                                    {isCompletada && (
                                      <span className="text-green-400 text-sm font-oranienbaum flex-shrink-0">✓ Hecho</span>
                                    )}
                                  </div>
                                );
                              })}

                              {/* Fallback if no piezas loaded yet */}
                              {piezas?.length === 0 && (
                                <button
                                  onClick={() => handleEmpezar(trabajo)}
                                  className="w-full py-4 rounded-xl font-oranienbaum font-bold text-black text-lg transition-all hover:opacity-90 active:scale-[0.98]"
                                  style={{ background: '#D6EF4E' }}
                                >
                                  EMPEZAR
                                </button>
                              )}
                            </div>

                            {/* Secondary actions */}
                            <div className="flex gap-2">
                              {trabajo?.estado === 'PENDIENTE' && (
                                <button
                                  onClick={() => handleMarcarRecibido(trabajo)}
                                  className="flex-1 py-2.5 rounded-xl border border-blue-500/30 text-blue-400 font-oranienbaum text-sm hover:bg-blue-500/10 transition-colors"
                                >
                                  📦 Recibido
                                </button>
                              )}
                              <button
                                onClick={() => handleIncidencia(trabajo)}
                                className="flex-1 py-2.5 rounded-xl border border-red-500/30 text-red-400 font-oranienbaum text-sm hover:bg-red-500/10 transition-colors"
                              >
                                ! Incidencia
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-white/30 font-oranienbaum">
                    {totalPiezasHoy === 0 ? '✓ No hay trabajos pendientes hoy' : 'Todos los trabajos en proceso o completados'}
                  </div>
                )}
              </div>
            )}

            {/* ── SEMANA view (mobile list) ── */}
            {mobileWeekView === 'semana' && (
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={goToPrevWeek} className="p-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/8 transition-colors">
                    <Icon name="ChevronLeft" size={16} />
                  </button>
                  <span className="flex-1 text-center text-white/50 text-xs font-oranienbaum">{weekLabel}</span>
                  <button onClick={goToNextWeek} className="p-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/8 transition-colors">
                    <Icon name="ChevronRight" size={16} />
                  </button>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
                  </div>
                ) : (
                  weekDates?.map((dateKey, i) => {
                    const dayTrabajos = getTrabajosByDate(dateKey);
                    const totalPiezas = dayTrabajos?.reduce((s, t) => s + (t?.cantidad_piezas || 1), 0);
                    const isToday = dateKey === todayKey;
                    return (
                      <div key={dateKey} className={`rounded-xl border overflow-hidden ${isToday ? 'border-[#D6EF4E]/25' : 'border-white/8'}`} style={{ background: '#111111' }}>
                        <div className={`px-4 py-2.5 flex items-center justify-between ${isToday ? 'bg-[#D6EF4E]/5' : ''}`}>
                          <div>
                            <span className={`font-oranienbaum font-bold text-sm ${isToday ? 'text-[#D6EF4E]' : 'text-white/80'}`}>{DIAS?.[i]}</span>
                            <span className="text-white/30 text-xs font-oranienbaum ml-2">
                              {format(weekDays?.[i], 'd MMM', { locale: es })}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-white/60 text-xs font-oranienbaum">{dayTrabajos?.length} trabajos · {totalPiezas} piezas</span>
                          </div>
                        </div>
                        {dayTrabajos?.length > 0 && (
                          <div className="px-4 pb-3 pt-1 space-y-1.5">
                            {dayTrabajos?.map(t => {
                              const piezas = t?.piezas || [];
                              const completadas = piezas?.filter(p => p?.estado === 'COMPLETADA')?.length;
                              const total = t?.cantidad_piezas || piezas?.length || 1;
                              const estadoCfg = ESTADO_CONFIG?.[t?.estado] || ESTADO_CONFIG?.PENDIENTE;
                              return (
                                <div key={t?.id} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t?.clienteColor }} />
                                    <span className="text-white/80 font-oranienbaum text-sm truncate">{t?.cliente?.nombre}</span>
                                    <span className="text-white/30 text-xs font-oranienbaum flex-shrink-0">{completadas}/{total}</span>
                                  </div>
                                  <span className={`text-xs flex-shrink-0 ${estadoCfg?.cls}`}>{estadoCfg?.icon}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {dayTrabajos?.length === 0 && (
                          <div className="px-4 pb-3 text-white/20 text-xs font-oranienbaum">Sin trabajos</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            PLANNING MODE — Desktop weekly board with cross-day DnD
        ══════════════════════════════════════════════════════════════════════ */}
        {viewMode === 'planificacion' && (
          <>
            {/* Filters */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 flex-shrink-0 overflow-x-auto">
              <button onClick={goToPrevWeek} className="p-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/8 transition-colors flex-shrink-0">
                <Icon name="ChevronLeft" size={16} />
              </button>
              <button onClick={goToToday} className="px-2.5 py-1 rounded-lg text-white/40 text-xs font-oranienbaum hover:bg-white/8 transition-colors flex-shrink-0">
                Hoy
              </button>
              <button onClick={goToNextWeek} className="p-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/8 transition-colors flex-shrink-0">
                <Icon name="ChevronRight" size={16} />
              </button>

              <div className="w-px h-4 bg-white/10 mx-1 flex-shrink-0" />

              <select
                value={filterCliente}
                onChange={e => setFilterCliente(e?.target?.value)}
                className="bg-[#111111] border border-white/10 text-white/60 text-xs rounded-lg px-2 py-1.5 font-oranienbaum focus:outline-none flex-shrink-0"
              >
                <option value="all">Todos los clientes</option>
                {clientes?.map(c => <option key={c?.id} value={c?.id}>{c?.nombre}</option>)}
              </select>

              <select
                value={filterEmpleado}
                onChange={e => setFilterEmpleado(e?.target?.value)}
                className="bg-[#111111] border border-white/10 text-white/60 text-xs rounded-lg px-2 py-1.5 font-oranienbaum focus:outline-none flex-shrink-0"
              >
                <option value="all">Todos los empleados</option>
                {empleados?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
              </select>

              <select
                value={filterEstado}
                onChange={e => setFilterEstado(e?.target?.value)}
                className="bg-[#111111] border border-white/10 text-white/60 text-xs rounded-lg px-2 py-1.5 font-oranienbaum focus:outline-none flex-shrink-0"
              >
                <option value="all">Todos los estados</option>
                <option value="PENDIENTE">○ Pendiente</option>
                <option value="RECIBIDO">📦 Recibido</option>
                <option value="EN_PROCESO">▶ En proceso</option>
                <option value="COMPLETADO">✓ Completado</option>
                <option value="INCIDENCIA">! Incidencia</option>
                <option value="CANCELADO">✕ Cancelado</option>
              </select>

              <button
                onClick={() => setFilterUrgentes(v => !v)}
                className={`px-2.5 py-1 rounded-lg text-xs font-oranienbaum border transition-colors flex-shrink-0 ${filterUrgentes ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'border-white/10 text-white/40 hover:border-white/20'}`}
              >
                Urgentes
              </button>

              {(filterCliente !== 'all' || filterEmpleado !== 'all' || filterEstado !== 'all' || filterUrgentes) && (
                <button
                  onClick={() => { setFilterCliente('all'); setFilterEmpleado('all'); setFilterEstado('all'); setFilterUrgentes(false); }}
                  className="px-2.5 py-1 rounded-lg text-xs font-oranienbaum text-white/30 hover:text-white/60 transition-colors flex-shrink-0"
                >
                  Limpiar
                </button>
              )}
            </div>

            {error && (
              <div className="mx-4 mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-oranienbaum">
                {error}
              </div>
            )}

            {copiedTrabajo && (
              <div className="mx-3 mt-2 px-3 py-2 rounded-lg bg-[#D6EF4E]/8 border border-[#D6EF4E]/20 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  <span className="text-[#D6EF4E] text-sm">📋</span>
                  <span className="text-white/70 text-sm font-oranienbaum">
                    Copiado: <strong className="text-white">{copiedTrabajo?.cliente?.nombre}</strong> · {copiedTrabajo?.producto?.nombre}
                  </span>
                </div>
                <button
                  onClick={() => setCopiedTrabajo(null)}
                  className="text-white/40 hover:text-white/80 text-xs font-oranienbaum flex-shrink-0"
                >
                  Cancelar
                </button>
              </div>
            )}

                        {/* ── Single DndContext wrapping all columns ── */}
            <div className="flex-1 overflow-x-auto overflow-y-auto p-3">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={pointerWithin}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex gap-2 min-w-max pb-4">
                    {weekDays?.map((dayDate, i) => {
                      const dateKey = toDateKey(dayDate);
                      const dayLabel = format(dayDate, 'd MMM', { locale: es });
                      return (
                        <DayColumn
                          key={dateKey}
                          dayIndex={i}
                          date={dateKey}
                          dayLabel={dayLabel}
                          trabajos={getTrabajosByDate(dateKey)}
                          isToday={dateKey === todayKey}
                          onDrop={handleDrop}
                          onEstadoChange={handleEstadoChange}
                          onMover={(t) => { setMovingTrabajo(t); setShowMoveModal(true); }}
                          onEditar={handleEditar}
                          onEliminar={handleEliminar}
                          onAjustarPiezas={handleAjustarPiezas}
                          onEmpezarPieza={handleEmpezarPieza}
                          onVerRegistro={handleVerRegistro}
                          onEmpezar={handleEmpezar}
                          onIncidencia={handleIncidencia}
                          onMarcarRecibido={handleMarcarRecibido}
                          onCopiar={handleCopiarTrabajo}
                          onAddTrabajo={handleAddTrabajo}
                          copiedTrabajo={copiedTrabajo}
                          onPegarTrabajo={handlePegarTrabajo}
                          onReorder={handleReorderDay}
                          onMoverAFecha={handleMoverAFecha}
                          activeId={activeId}
                          overId={activeId}
                        />
                      );
                    })}
                  </div>

                  {/* DragOverlay — ghost card while dragging */}
                  <DragOverlay>
                    {activeTrabajo ? (
                      <div style={{ opacity: 0.85, width: '155px' }}>
                        <TrabajoCard
                          trabajo={activeTrabajo}
                          piezas={activeTrabajo?.piezas || []}
                          clienteColor={activeTrabajo?.clienteColor || '#6B7280'}
                          isOpen={false}
                          onToggle={() => {}}
                          isDragging={true}
                          onSelectPieza={() => {}}
                          loadingPiezaId={null}
                        />
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            </div>

            {/* Unscheduled tray — also a drop target */}
            <div
              className="flex-shrink-0 border-t border-white/8 px-4 py-3"
              style={{ background: '#0D0D0D' }}
              onDragOver={(e) => e?.preventDefault()}
              onDrop={(e) => {
                e?.preventDefault();
                const trabajoId = e?.dataTransfer?.getData('trabajoId');
                if (trabajoId) planTrabajoService?.moveToDate(trabajoId, null)?.then(loadData);
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-white/40 text-xs font-oranienbaum uppercase tracking-widest">Trabajos sin programar</span>
                {unscheduled?.length > 0 && (
                  <span className="bg-white/10 text-white/50 text-[10px] font-oranienbaum px-1.5 py-0.5 rounded-full">{unscheduled?.length}</span>
                )}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {unscheduled?.length === 0 ? (
                  <div className="text-white/20 text-xs font-oranienbaum py-2">Sin trabajos pendientes de programar</div>
                ) : (
                  unscheduled?.map(t => (
                    <div key={t?.id} className="flex-shrink-0 w-52">
                      <TrabajoCard
                        trabajo={t}
                        piezas={t?.piezas || []}
                        clienteColor={t?.clienteColor || '#6B7280'}
                        onEstadoChange={handleEstadoChange}
                        onMover={(t) => { setMovingTrabajo(t); setShowMoveModal(true); }}
                        onEditar={handleEditar}
                        onEliminar={handleEliminar}
                        onAjustarPiezas={handleAjustarPiezas}
                        onEmpezarPieza={handleEmpezarPieza}
                        onVerRegistro={handleVerRegistro}
                        onEmpezar={handleEmpezar}
                        onIncidencia={handleIncidencia}
                        onMarcarRecibido={handleMarcarRecibido}
                        onCopiar={handleCopiarTrabajo}
                        onMoverAFecha={handleMoverAFecha}
                        onSelectPieza={() => {}}
                        loadingPiezaId={null}
                        onDragStart={() => {}}
                        onDragEnd={() => {}}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <AddTrabajoModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleConfirmAdd}
        preselectedDate={addModalDate}
        weekDates={weekDates}
      />
      <EditTrabajoModal
        isOpen={showEditModal}
        trabajo={editingTrabajo}
        onClose={() => { setShowEditModal(false); setEditingTrabajo(null); }}
        onSaved={handleSaveEdit}
        weekDates={weekDates}
      />
      <DeleteTrabajoModal
        isOpen={showDeleteModal}
        trabajo={deletingTrabajo}
        onClose={() => { setShowDeleteModal(false); setDeletingTrabajo(null); }}
        onDelete={handleConfirmDelete}
        onCancel={handleConfirmCancel}
      />
      <MoveModal
        isOpen={showMoveModal}
        trabajo={movingTrabajo}
        weekDates={weekDates}
        onClose={() => { setShowMoveModal(false); setMovingTrabajo(null); }}
        onMove={handleMove}
      />
      <IncidenciaModal
        isOpen={showIncidenciaModal}
        trabajo={incidenciaTrabajo}
        onClose={() => { setShowIncidenciaModal(false); setIncidenciaTrabajo(null); }}
        onSaved={loadData}
      />
    </>
  );
};

export default PlanTrabajo;
